import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { planApi } from '@/api/planApi'
import { useNavigate } from 'react-router-dom'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { usePlanDialogStore } from '@/stores/planDialogStore'
import { useCan } from '@/hooks/useCan'
import { useBillingEnabled } from '@/hooks/useBillingEnabled'
import { morphToast } from '@/components/ui/Toast'
import { PlanSelectionDialog } from '@/components/billing/PlanSelectionDialog'
import { CancelPlanDialog } from '@/components/billing/CancelPlanDialog'
import { redirectToCheckout } from '@/utils/checkout'
import type { ChangePlanRequest, Plan } from '@/api/types'

export function GlobalPlanDialog() {
  const { t } = useTranslation()
  const open = usePlanDialogStore((s) => s.open)
  const closeDialog = usePlanDialogStore((s) => s.closeDialog)
  const navigate = useNavigate()
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const subscription = useWorkspaceStore((s) => s.subscription)
  const loadSubscription = useWorkspaceStore((s) => s.loadSubscription)
  const changePlan = useWorkspaceStore((s) => s.changePlan)
  const cancelSubscription = useWorkspaceStore((s) => s.cancelSubscription)
  const billingEnabled = useBillingEnabled()
  // Owner rights alone are not enough while payments are under development.
  const canChangePlan = useCan('change_plan') && billingEnabled

  const [plans, setPlans] = useState<Plan[]>([])
  const [loadingPlans, setLoadingPlans] = useState(false)
  const [changingPlanId, setChangingPlanId] = useState<number | null>(null)
  const [warningDialogOpen, setWarningDialogOpen] = useState(false)
  const [warningDialogLoading, setWarningDialogLoading] = useState(false)
  const [pendingPlan, setPendingPlan] = useState<Plan | null>(null)

  useEffect(() => {
    if (!open || !currentWorkspace?.id) return
    void loadSubscription(currentWorkspace.id)
    setLoadingPlans(true)
    planApi
      .list()
      .then((list) => setPlans(list.filter((plan) => plan.is_active)))
      .catch(() => undefined)
      .finally(() => setLoadingPlans(false))
  }, [open, currentWorkspace?.id, loadSubscription])

  const currentPlanCode = subscription?.plan.code ?? null
  const isPaidPlan = currentPlanCode != null && currentPlanCode !== 'free'
  const freePlan = plans.find((plan) => plan.code.toLowerCase() === 'free') ?? null

  function isDowngrade(targetPlan: Plan): boolean {
    if (!subscription) return false
    return (targetPlan.price_cents ?? 0) < (subscription.plan.price_cents ?? 0)
  }

  /** Run a change-plan request; if it returns a checkout URL, redirect to it. */
  async function applyChange(plan: Plan, body: ChangePlanRequest): Promise<boolean> {
    const result = await changePlan(body)
    if (result.checkout_url) {
      redirectToCheckout(result.checkout_url)
      return true
    }
    if (result.pending_confirmation) {
      // Este diálogo não tem tela de espera própria: manda para a de assinatura,
      // que sabe aguardar a confirmação do pagamento.
      const slug = useWorkspaceStore.getState().currentWorkspace?.slug
      if (slug) {
        closeDialog()
        navigate(`/workspace/${slug}/subscription?status=success`)
        return true
      }
    }
    morphToast.success(t('billing.planChanged', { name: plan.name }))
    return false
  }

  async function handleSelectPlan(plan: Plan) {
    if (!canChangePlan) return
    if (subscription?.plan.id === plan.id) return

    if (plan.code.toLowerCase() === 'free' && isPaidPlan) {
      setPendingPlan(null)
      setWarningDialogOpen(true)
      return
    }

    if (isPaidPlan && isDowngrade(plan)) {
      setPendingPlan(plan)
      setWarningDialogOpen(true)
      return
    }

    setChangingPlanId(plan.id)
    try {
      const redirected = await applyChange(plan, { plan_code: plan.code })
      if (!redirected) closeDialog()
    } catch (err) {
      morphToast.fromError(err)
    } finally {
      setChangingPlanId(null)
    }
  }

  async function handleConfirmWarningDialog() {
    if (!canChangePlan) return
    setWarningDialogLoading(true)
    try {
      if (pendingPlan === null) {
        await cancelSubscription()
        morphToast.success(
          t('billing.cancelSuccess', 'Assinatura cancelada. O plano ficará ativo até o final do período.'),
        )
      } else {
        setChangingPlanId(pendingPlan.id)
        await applyChange(pendingPlan, { plan_code: pendingPlan.code })
      }
      setWarningDialogOpen(false)
      closeDialog()
    } catch (err) {
      morphToast.fromError(err)
    } finally {
      setWarningDialogLoading(false)
      setChangingPlanId(null)
    }
  }

  function handleCancelPlan() {
    setPendingPlan(null)
    setWarningDialogOpen(true)
  }

  if (!currentWorkspace) return null

  return (
    <>
      <PlanSelectionDialog
        open={open}
        onClose={closeDialog}
        plans={plans}
        loading={loadingPlans}
        canChangePlan={canChangePlan}
        billingEnabled={billingEnabled}
        currentPlanCode={subscription?.plan.code ?? null}
        cancelAtPeriodEnd={subscription?.cancel_at_period_end === true}
        currentPeriodEnd={subscription?.current_period_end ?? null}
        canceledAt={subscription?.canceled_at ?? null}
        changingPlanId={changingPlanId}
        onSelect={handleSelectPlan}
        onCancelPlan={handleCancelPlan}
        showCancelAction={
          canChangePlan &&
          !!subscription &&
          !subscription.cancel_at_period_end &&
          subscription.plan.code.toLowerCase() !== 'free'
        }
      />

      {warningDialogOpen && subscription ? (
        <CancelPlanDialog
          open={warningDialogOpen}
          subscription={subscription}
          currentPlan={subscription.plan}
          targetPlan={pendingPlan}
          freePlan={freePlan}
          onConfirm={() => void handleConfirmWarningDialog()}
          onClose={() => {
            if (!warningDialogLoading) setWarningDialogOpen(false)
          }}
          loading={warningDialogLoading}
        />
      ) : null}
    </>
  )
}
